import { DOCUMENT, inject, Injectable, PLATFORM_ID } from '@angular/core';

import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../../environments/environment';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export type SignupMethod = 'email' | 'google' | 'linkedin' | 'microsoft';

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManager {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly gtmId = environment.gtmId;
  private readonly scriptId = 'google-tag-manager-script';

  private initialized = false;

  initialize(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (this.initialized) {
      return;
    }

    if (this.document.getElementById(this.scriptId)) {
      this.initialized = true;
      return;
    }

    this.initialized = true;

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
    });

    const script = this.document.createElement('script');

    script.id = this.scriptId;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(this.gtmId)}`;

    script.onerror = (): void => {
      console.error('Google Tag Manager failed to load.');
      this.initialized = false;
    };

    this.document.head.appendChild(script);
  }

  pushEvent(event: string, parameters: Record<string, unknown> = {}): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event,
      ...parameters,
    });
  }

  trackExternalSignupLanding(signupSource: string, signupClickId: string): void {
    this.pushEvent('external_signup_landing', {
      signup_source: signupSource,
      signup_click_id: signupClickId,
    });
  }

  trackSignupComplete(
    signupSource: string,
    signupClickId: string,
    signupMethod: SignupMethod,
  ): void {
    this.pushEvent('signup_complete', {
      signup_source: signupSource,
      signup_click_id: signupClickId,
      signup_method: signupMethod,
    });
  }
}
